<template>
  <!-- Note: 'persistent' is a workaround for
  https://github.com/vuetifyjs/vuetify/issues/5200 -->
  <v-dialog
    lazy
    scrollable
    persistent
    transition="dialog-bottom-transition"
    :value="show"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    :width="720"
    @input="(val) => $emit('update:show', val)"
  >
    <v-card v-if="entries && entries.length">
      <v-card-title class="pb-0">
        <v-layout align-center>
          <v-icon class="mr-2">
            timer
          </v-icon>
          <div class="headline">
            {{ numEntriesToTime }}
          </div>
          <v-btn
            class="ml-auto mr-0 my-0"
            :disabled="updating"
            flat
            icon
            :title="$t('cancel')"
            @click="close()"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-layout>
      </v-card-title>
      <v-card-text>
        <v-layout column>
          <div v-if="contentIncludesClosingTimeRadioSpots">
            <div
              v-if="closingtimeEntryUpdateNotPossibleAsStoreOpeninghoursMissing"
              class="warning--text mb-4"
            >
              {{ $t('openinghoursUpdateWarningStoreOpeninghoursMissing') }}
              <router-link to="/openinghours">
                {{ $t('openinghoursEdit') }}
              </router-link>
            </div>
            <!-- Closingtime Time Widget -->
            <closingtime-controls
              ref="closingtimecontrols"
              :minutes-before-closing.sync="minutesBeforeClosing"
              :week-days.sync="weekDays"
              :form-is-valid.sync="closingTimeFormIsValid"
            />
          </div>
          <!-- Start/Stopdate Controls-->
          <v-layout
            v-if="!contentsAreOnlyClosingTimeRadioSpots"
            :wrap="$vuetify.breakpoint.mdAndDown"
            align-center
            class="mt-2"
          >
            <!-- Startdate -->
            <div class="menu-container">
              <v-menu
                v-model="startDateMenu"
                class="mr-3"
                lazy
                offset-y
                :min-width="mobileDatepickerMinWidth"
                :nudge-left="40"
              >
                <v-text-field
                  slot="activator"
                  v-model="startDate"
                  prepend-icon="event"
                  readonly
                  :label="$t('startdate')"
                />
                <v-date-picker
                  v-model="startDate"
                  color="msr"
                  scrollable
                  event-color="msr"
                  :events="isEventForChosenDateRange"
                  :no-title="$vuetify.breakpoint.xsOnly"
                  :first-day-of-week="1"
                  :picker-date="startDateInitialPickerDate"
                  :locale="$t('_datepickerLocale')"
                  :allowed-dates="isAllowedStartDateWithinHardRange"
                />
              </v-menu>
            </div>
            <!-- Stopdate -->
            <div class="menu-container">
              <v-menu
                v-model="stopDateMenu"
                lazy
                offset-y
                :min-width="mobileDatepickerMinWidth"
                :nudge-left="40"
              >
                <v-text-field
                  slot="activator"
                  v-model="stopDate"
                  :label="$t('toIncluding')"
                  :placeholder="stopDatePlaceHolder"
                  prepend-icon="event"
                  readonly
                />
                <v-date-picker
                  v-model="stopDate"
                  color="msr"
                  scrollable
                  event-color="msr"
                  :events="isEventForChosenDateRange"
                  :no-title="$vuetify.breakpoint.xsOnly"
                  :first-day-of-week="1"
                  :locale="$t('_datepickerLocale')"
                  :allowed-dates="isAllowedStopDateWithinHardExpiry"
                />
              </v-menu>
              <v-btn
                v-if="stopDate"
                flat
                icon
                @click="stopDate = ''"
              >
                <v-icon class="msr--text">
                  clear
                </v-icon>
              </v-btn>
            </div>
            <v-layout
              column
              :align-end="$vuetify.breakpoint.smAndUp"
              :align-start="$vuetify.breakpoint.xsOnly"
              :class="{'ml-2': $vuetify.breakpoint.smAndUp}"
            >
              <!-- Last Dates used -->
              <last-initial-dates-chip
                v-if="
                  showLastInitialDatesChip &&
                    !contentsAreOnlyClosingTimeRadioSpots
                "
                @set-dates="setInitialDates"
              />
              <!-- Remove Timing button -->
              <v-btn
                v-if="atLeastOneEntryWithTiming"
                flat
                round
                small
                :title="$t('deleteTiming')"
                @click="showConfirmRemoveMultipleDialog = true"
              >
                {{ $t('deleteTiming') }}
              </v-btn>
            </v-layout>
          </v-layout>
          <hard-range-warning-list-tile
            v-if="firstEntry && firstEntry.spot"
            :title="firstEntry.spot.title"
            :hard-start="firstEntry.spot.hard_start"
            :hard-expire="firstEntry.spot.hard_expire"
            :chosen-startdate="startDate"
            :chosen-stopdate="stopDate"
          />
          <!-- Timeframe Select -->
          <timeframe-controls
            v-if="$store.getters.userCanSetTimeFrames && showTimeframeControls"
            ref="timeframecontrols"
            class="mt-2"
          />
          <!-- Remove multiple Dialog -->
          <v-dialog v-model="showConfirmRemoveMultipleDialog">
            <v-card>
              <v-card-title>
                <div
                  class="headline"
                  style="max-width: calc(100% - 32px)"
                >
                  {{ $t('askDeleteTiming') }}
                </div>
                <v-btn
                  flat
                  icon
                  style="position: absolute; top: 0; right: 0"
                  :title="$t('abort')"
                  @click="showConfirmRemoveMultipleDialog = false"
                >
                  <v-icon>close</v-icon>
                </v-btn>
              </v-card-title>
              <v-card-actions>
                <v-spacer />
                <btn-ok
                  :title="$t('confirm')"
                  round
                  @click="removeEntriesTiming(entries)"
                />
                <v-spacer />
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-layout>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <btn-ok
          :loading="updating"
          :disabled="!formIsValid"
          round
          data-cy="entry-timing-dialog-confirm-btn"
          :title="$t('confirm')"
          @click="submit()"
        />
        <v-spacer />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import entryCreationUpdateDialogClosingTime from '@/mixins/entryCreationUpdateDialogClosingTime.js';
import identifiers from '@/mixins/identifiers.js';
import mobileDatepickerMinWidth from '@/mixins/mobileDatepickerMinWidth.js';
import timing from '@/mixins/timing.js';

import BtnOk from '@/components/ButtonOk';
import ClosingtimeControls from '@/components/ClosingtimeControls';
import HardRangeWarningListTile from '@/components/HardRangeWarningListTile';
import LastInitialDatesChip from '@/components/LastInitialDatesChip';
import TimeframeControls from '@/components/TimeframeControls';

export default {
  components: {
    BtnOk,
    ClosingtimeControls,
    HardRangeWarningListTile,
    LastInitialDatesChip,
    TimeframeControls,
  },
  mixins: [
    entryCreationUpdateDialogClosingTime,
    identifiers,
    mobileDatepickerMinWidth,
    timing,
  ],
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    showTimeframeControls: {
      type: Boolean,
      default: true,
    },
    /* A list of RadioSpotEntry, TVSpotEntry, TickerEntry objects. */
    entries: {
      type: Array[Object],
      required: true,
    },
  },
  data() {
    return {
      updating: false,
      showConfirmRemoveMultipleDialog: false,
      startDateMenu: false,
      stopDateMenu: false,
      // Note: Via mixins we are adding some more fields like:
      //  - weekDays
      //  - minutesBeforeClosing
    };
  },
  computed: {
    todayIsoString() {
      return new Date().toISOString();
    },
    /**
     * Ensure the date picker doesn't focus on past date.
     *
     * It can be very confusing to have to scroll forward 2 years just
     * to pick 'today'.
     */
    startDateInitialPickerDate() {
      if (!!this.startDate && this.isInPast(this.startDate)) {
        return this.todayIsoString;
      }
      return undefined;
    },
    closingtimeEntryUpdateNotPossibleAsStoreOpeninghoursMissing() {
      return (
        this.entries.length &&
        this.entries.filter(
          (entry) =>
            this.isClosingTimeRadioSpotEntry(entry) &&
            !entry.decoder.store.has_opening_hours
        ).length > 0
      );
    },
    formIsValid() {
      return this.closingTimeFormIsValid;
    },
    firstEntry() {
      if (!this.entries.length) {
        return null;
      }
      return this.entries[0];
    },
    numEntriesToTime() {
      if (!this.entries.length) {
        return '';
      }

      let template = this.$t('numEntriesToTime');
      if (this.$route.name === 'spot') {
        template = this.$t('numDecodersToTime');
      }
      return template.replace('{{ num }}', this.entries.length);
    },
    atLeastOneEntryWithTiming() {
      let timingFound = false;
      this.entries.forEach((entry) => {
        if (entry.has_timing) {
          timingFound = true;
          return;
        }
      });
      return timingFound;
    },
  },
  watch: {
    /**
     * Set initial dates based on first entry given.
     *
     * For editing a single entry we are still passing a list, so the
     * first entry still works for us as the source here.
     */
    show: function (show) {
      if (show) {
        this.$nextTick(() => {
          if (this.firstEntry) {
            this.setStartStopDateValuesFromEntry(this.firstEntry);
            this.setClosingtimeValuesFromEntry(this.firstEntry);
            this.setTimeframeValueFromEntry(this.firstEntry);
          }
        });
      }
    },
  },
  methods: {
    submit() {
      this.rememberLastInitialDates();

      const opts = {
        startDate: this.startDate,
        stopDate: this.stopDate,
        timeframeIds: null,
        // Closingtime Radiospot Entry only:
        weekDays: this.weekDays,
      };

      const closingtimeControls = this.$refs.closingtimecontrols;
      if (closingtimeControls) {
        opts.minutesBeforeClosing = closingtimeControls.minutesBeforeClosing;
      }

      const timeframeControls = this.$refs.timeframecontrols;
      if (timeframeControls) {
        if (
          !timeframeControls.expanded ||
          timeframeControls.selectedTimeframes.length == 0
        ) {
          // Switched off? Remove any timeframes we may have set already.
          opts.timeframeIds = '';
        } else {
          // Otherwise set existing/new timeframes.
          opts.timeframeIds = timeframeControls.selectedTimeframes
            .map((x) => x.id)
            .join(',');
        }
      }

      this.updateEntriesTiming(this.entries, opts).then(() => {
        this.emitSubmitted();
        this.reset();
        this.close();
      });
    },
    reset() {
      this.updating = false;
      this.resetDates();
    },
    close() {
      this.$emit('update:show', false);
    },
    emitSubmitted() {
      this.$emit('submitted');
    },
    /**
     * Set received dates as initial values for pickers.
     *
     * Ignore the time suffix (e.g. "2016-04-18T10:03:47").
     */
    setStartStopDateValuesFromEntry(entry) {
      if (entry.startdate) {
        this.startDate = this.formatDate(entry.startdate);
      }
      if (entry.stopdate) {
        this.stopDate = this.formatDate(entry.stopdate);
      }
    },
    setClosingtimeValuesFromEntry(entry) {
      if (entry.weekdays) {
        this.weekDays = entry.weekdays;
      }
      if (entry.minutes_before_closing) {
        this.minutesBeforeClosing = entry.minutes_before_closing;
      }
    },
    setTimeframeValueFromEntry(entry) {
      const timeframeControls = this.$refs.timeframecontrols;
      if (timeframeControls) {
        timeframeControls.initializeWith(entry.timeframes);
      }
    },
    /**
     * Update entries and go back to previous route on success.
     */
    updateEntriesTiming(entries, opts) {
      if (!opts) {
        opts = {};
      }

      if (!opts.successMessage) {
        opts.successMessage = 'timingUpdatedSuccess';
      }

      const payload = {
        startdate: opts.startDate,
        stopdate: opts.stopDate,
      };

      if (opts.weekDays) {
        payload.weekdays = opts.weekDays.join(',');
      }
      if (opts.minutesBeforeClosing) {
        payload.closingtime_minutes_before_closing = opts.minutesBeforeClosing;
      }

      // Passing null will not change the timeframes, empty string will
      // disassociate them, and CSV of ids will associate to those objects.
      if (opts.timeframeIds !== null) {
        payload['timeframe_ids'] = opts.timeframeIds;
      }

      const entryIdents = entries.map((entry) => entry.ident);

      this.updating = true;
      const promise = this.$api.batchUpdateEntries(entryIdents, payload);

      promise
        .then((responses) => {
          this.$bus.$emit('alert-success', {
            message: opts.successMessage,
            timeout: 2000,
            translate: true,
          });
          this.reset();
          this.close();
        })
        .catch((error) => {
          let message = 'genericFailFeedback';
          let timeout = 1500;

          if (
            error &&
            error.response &&
            error.response.data &&
            error.response.data.detail
          ) {
            message = error.response.data.detail;
            timeout = 15000;
          }

          this.$bus.$emit('alert-error', {
            message: message,
            translate: true,
            timeout: timeout,
          });
          this.reset();
        });
      return promise;
    },
    removeEntriesTiming(entries) {
      this.showConfirmRemoveMultipleDialog = false;
      const opts = {
        startDate: '',
        stopDate: '',
        successMessage: 'timingRemovedSuccess',
      };
      this.updateEntriesTiming(entries, opts).then(() => {
        this.emitSubmitted();
        this.reset();
        this.close();
      });
    },
  },
};
</script>

<style scoped>
.menu-container {
  display: flex;
  align-items: baseline;
}

.back-btn {
  margin: 0;
  padding: 0;
}

.entry-headers {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  height: auto;
  padding: 0;
}

.entry-item {
  margin: 0.1em 0 0.1em 0;
}
.entry-item.last {
  margin-right: 0;
}

.entry-item-group {
  display: flex;
  flex-flow: row nowrap;
}
.entry-item-group.last {
  justify-content: flex-end;
  align-items: flex-start;
}

@media (max-width: 599px) {
  .entry-item-group {
    flex-flow: column nowrap;
  }
  .entry-item-group.last {
    justify-content: flex-start;
    align-items: flex-end;
  }
}

.category {
  min-width: 80px;
}

.preview {
  display: flex;
  align-items: center;
  max-height: 72px;
  width: 128px;
  margin-right: 1em;
}

.timing {
  display: flex;
  align-items: center;
}

.i2 i {
  font-size: 2em;
}

.date-pickers {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.dialog-title {
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>



// WEBPACK FOOTER //
// src/components/EntryTimingDialog.vue